<template>
  <div
    class="default-layout"
    :class="{ 'default-layout--reservations': route.name === 'reservations' }"
  >
    <div class="main">
      <component
        :is="sidebarComponent"
        :class="{ 'reservations-sidebar': route.name === 'reservations' }"
      />
      <div
        :class="['js-container container', { subscription: route.fullPath.includes('sign/subscription') }]"
        @scroll="onScroll"
      >
        <component
          :is="navbarComponent"
          :class="{ onScroll: !topOfPage }"
        />

        <div
          v-if="isNewDesign"
          class="route-name"
        >
          <DropdownMenu
            v-if="hasSubmenu"
            align="right"
          >
            <template #trigger>
              <q-button
                variant="tertiary"
                size="m"
              >
                <v-icon
                  name="hamburger-menu"
                  width="20"
                  height="20"
                  stroke="transparent"
                  action-color="transparent"
                />
              </q-button>
            </template>

            <template #menu="{ close }">
              <DropdownMenuItem
                v-for="r in submenuRoutes"
                :key="r.path"
                :label="$t(r.meta?.menuItems)"
                :icon="r.meta?.icon"
                width="20"
                height="20"
                @click="goToRoute(r.name, close)"
              />
            </template>
          </DropdownMenu>

          <span> {{ routeName }}</span>
        </div>
        <div
          class="content"
          :class="{ 'content--orders': route.fullPath.includes('crm/orders') && !route.fullPath.includes('create') }"
        >
          <router-link
            v-if="route.name === 'reservations'"
            class="reservations-mobile-back"
            :to="{ name: 'main', params: { locale: route.params.locale } }"
          >
            ← {{ $t('leftSideMenu.main') }}
          </router-link>
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import NavBarTop from '@/components/app/nav/NavBarTop.vue';
import NavBarTopNew from '@/components/app/nav/NavBarTopNew.vue';
import SideBarLeft from '@/components/app/sidebar/SideBarLeft.vue';
import SideBarLeftNew from '@/components/app/sidebar/SideBarLeftNew.vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import i18n from '@/i18n';
import DropdownMenu from '@/components/UI/DropdownMenu.vue';
import DropdownMenuItem from '@/components/UI/DropdownMenuItem.vue';

const topOfPage = ref(true);

const route = useRoute();
const router = useRouter();
const store = useStore();

const isNewDesign = computed(() => store.getters['featureFlags/isNewDesign']);
const navbarComponent = computed(() => (isNewDesign.value ? NavBarTopNew : NavBarTop));
const sidebarComponent = computed(() => (isNewDesign.value ? SideBarLeftNew : SideBarLeft));

const submenuRoutes = computed(() => {
  // Берём роут раздела первого уровня под '/:locale'
  // matched[0] — корневой '/:locale', matched[1] — раздел (crm, loyalty, statistic и т.п.)
  const sectionRoute = route.matched?.[1];
  if (!sectionRoute || !Array.isArray(sectionRoute.children)) {
    return [];
  }
  return sectionRoute.children.filter((child) => child.meta?.showInMenu);
});
const hasSubmenu = computed(() => submenuRoutes.value.length > 0);

// Safely get the route name for display
const routeName = computed(() => {
  const menuItems = route.meta?.menuItems;
  if (menuItems && typeof menuItems === 'string') {
    try {
      return i18n.global.t(menuItems);
    } catch (error) {
      console.warn('Translation key not found:', menuItems);
      return '';
    }
  }
  return '';
});

function goToRoute(name, closeMenu) {
  router.push({ name });
  closeMenu();
}
</script>

<style lang="scss" scoped>
.main {
  position: relative;
  display: flex;
  flex: 1;
  overflow: auto;
  max-height: 100vh;
}

.default-layout {
  display: flex;
  background: var(--color-gray-light);
}

.reservations-mobile-back { display: none; }
.default-layout--reservations {
  .container, .content { min-width: 0; }
  @media screen and (max-width: 767px) {
    .reservations-sidebar { display: none; }
    .content { padding: 8px; margin: 8px; }
    .reservations-mobile-back {
      display: inline-flex;
      align-items: center;
      align-self: flex-start;
      min-height: 44px;
      padding: 0 12px;
      margin-bottom: 8px;
      color: #0e0e22;
      background: #f5f5f5;
      border-radius: 8px;
      font: 500 14px/1.4 'qb Inter', Inter, sans-serif;
    }
  }
}

.container {
  position: relative;
  background-color: var(--color-gray-light);
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100vh;
  min-height: 100%;

  /* padding: 0 32px 27.5px 32px; */
  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
}

.content {
  position: relative;
  min-width: 1024px;
  padding: 16px;
  margin: 16px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0px 4px 16px 0px rgba(14, 14, 34, 0.04);
  /* flex: 1 1 auto; */
  /* height: 100%; */
  flex: 1;
  // flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0px;
    /* Ширина всего элемента навигации */
  }

  &--orders {
    @media screen and (max-width: 1280px) {
      min-width: unset;
    }
  }
}

.navbar {
  &.onScroll {
    background: #ffffff !important;
    -webkit-box-shadow: 0 4px 6px -6px #222;
    -moz-box-shadow: 0 4px 6px -6px #222;
    box-shadow: 0 4px 7px -6px #222;
    transition: 0.7s;
  }
}

.my-scroll-up {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  left: 75vw;
  top: 50vh;
  z-index: 99;
  height: 55px;
  width: 55px;
  background-color: rgba(0, 0, 0, 0.658);
  border: rgba(0, 0, 0, 0.658);
  border-radius: 100px;
  color: #ffffff;
  cursor: pointer;
}

.scroll-animation::after {
  content: '';
  height: 25px;
  width: 10px;
  display: block;
  margin: 0 auto;
  border: 2px solid #ffffff;
  border-radius: 10px;
}

.scroll-animation::before {
  content: '';
  display: block;
  height: 10px;
  width: 0;
  left: 49%;
  animation: 2s top infinite;
  border: 1px solid #ffffff;
  border-radius: 10px;
  position: absolute;
}

@keyframes top {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.subscription {
  background-image: url(@/assets/img/vase.svg);
  background-repeat: no-repeat;
  background-color: var(--color-gray-light);
  background-position: calc(100%) 0%;
}

.route-name {
  padding: 8px 20px 0px 20px;
  color: rgba(14, 14, 34, 0.6);
  font-family: Montserrat;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
